import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AuthContext, API } from '../App';
import {
  FileText, PenLine, Linkedin, Briefcase, Star,
  Phone, LogOut, Loader2, Crown, PlayCircle, Lock, Check, Target,
} from 'lucide-react';

const PLAN_COLOR = {
  free: { border: 'border-gray-300', badge: 'bg-gray-100 text-gray-700' },
  pro: { border: 'border-blue-300', badge: 'bg-blue-100 text-blue-800' },
  career_boost: { border: 'border-[#009639]', badge: 'bg-green-100 text-green-800' },
  elite: { border: 'border-[#CE1126]', badge: 'bg-red-100 text-red-800' },
  career_success_pro: { border: 'border-[#CE1126]', badge: 'bg-amber-100 text-amber-800' },
};

const AI_TOOLS = [
  { key: 'mock_interview', to: '/ai/mock-interview', icon: Briefcase, label: 'AI Mock Interview', desc: 'Role-based questions + AI feedback' },
  { key: 'voice_interview', to: '/ai/voice-interview', icon: Phone, label: 'AI Voice Interview', desc: 'Speak answers, get scored' },
  { key: 'ai_coach', to: '/ai/coach', icon: Star, label: 'Advanced AI Career Coach', desc: 'Personalized UAE job-search guidance' },
  { key: 'tracker', to: '/tracker', icon: PenLine, label: '30-Day Job Search Tracker', desc: 'Daily plan + application log' },
];

// Functional, current-package tools only. Legacy standalone offers removed.
const TOOLS = [
  { benefit: 'ats_quick_check',      to: '/cv-checker',  icon: FileText, label: 'ATS CV Checker',            desc: 'Check your CV score', tier: 'Free' },
  { benefit: 'cv_builder',           to: '/cv-builder',  icon: PenLine,  label: 'Create CV From Scratch',    desc: '7-step guided CV builder', tier: 'Pro' },
  { benefit: 'cv_optimize_existing', to: '/cv-checker',  icon: FileText, label: 'Optimize Existing CV',      desc: 'Upload & professionally optimize', tier: 'Pro' },
  { benefit: 'cv_download_pdf_docx', to: '/cv-builder',  icon: FileText, label: 'My CV Versions',            desc: 'Download PDF / DOCX', tier: 'Pro' },
  { benefit: 'ats_jd_match_full',    to: '/cv-checker',  icon: Target,   label: 'Job-Specific CV Optimization', desc: 'Tailor your CV to a job description', tier: 'Career Boost' },
  { benefit: 'linkedin_optimization', to: '/linkedin-tips', icon: Linkedin, label: 'LinkedIn Optimization',  desc: 'Headline, About, skills & keywords', tier: 'Career Boost' },
];

export default function DashboardPage() {
  const { user, logout } = useContext(AuthContext);
  const nav = useNavigate();
  const [ent, setEnt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftInfo, setDraftInfo] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios.get(`${API}/entitlements/me`)
      .then(r => setEnt(r.data))
      .catch(() => setEnt({ plan: 'free', label: 'Free', benefits: ['ats_quick_check'], benefit_map: {}, career_calls: null, expires_at: null }))
      .finally(() => setLoading(false));
    axios.get(`${API}/cv-builder/draft`)
      .then(r => { if (r.data?.draft) setDraftInfo(r.data.draft); })
      .catch(() => {});
    axios.get(`${API}/ai/usage`)
      .then(r => setAiUsage(r.data))
      .catch(() => {});
    axios.get(`${API}/plans`)
      .then(r => setPlans(r.data?.plans || []))
      .catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" /></div>;

  const plan = ent?.plan || 'free';
  const colors = PLAN_COLOR[plan] || PLAN_COLOR.free;
  const availableTools = TOOLS.filter(t => ent?.benefit_map?.[t.benefit]);
  const lockedTools = TOOLS.filter(t => !ent?.benefit_map?.[t.benefit]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold">Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
            <p className="text-gray-500 text-sm">{user?.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { logout(); nav('/'); }} data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-1" />Log Out
          </Button>
        </div>

        {/* Resume Draft banner */}
        {draftInfo && (
          <Card className="p-4 mb-6 border-2 border-[#009639] bg-green-50 flex flex-wrap items-center justify-between gap-3" data-testid="resume-draft-banner">
            <div className="flex items-center gap-3">
              <PlayCircle className="w-6 h-6 text-[#009639]" />
              <div>
                <p className="font-semibold text-sm">You have an unfinished CV draft</p>
                <p className="text-xs text-gray-600">Step {draftInfo.current_step || 1} of 7 — pick up right where you left off.</p>
              </div>
            </div>
            <Link to="/cv-builder">
              <Button className="btn-uae-green" size="sm" data-testid="resume-draft-btn">Resume CV Draft</Button>
            </Link>
          </Card>
        )}

        {/* My Active Package */}
        <h2 className="text-lg font-bold mb-3">My Active Package</h2>
        <Card className={`p-5 mb-8 border-2 ${colors.border}`} data-testid="current-plan-card">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-[#CE1126]" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Current Plan</p>
                <p className="text-2xl font-bold flex items-center gap-2">
                  {ent.label}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge}`} data-testid="plan-badge">
                    {plan === 'free' ? 'Free' : 'Active'}
                  </span>
                </p>
                <p className="text-xs text-gray-500">{ent.positioning}</p>
              </div>
            </div>
            <div className="text-right">
              {ent.expires_at && <p className="text-xs text-gray-500">Valid until {new Date(ent.expires_at).toLocaleDateString()}</p>}
              {plan === 'free' && <Link to="/pricing"><Button className="btn-uae-red" size="sm" data-testid="plan-upgrade-btn">Upgrade</Button></Link>}
            </div>
          </div>

          {ent.career_calls && (
            <div className="mt-4 pt-4 border-t flex items-center gap-3" data-testid="career-calls">
              <Phone className="w-5 h-5 text-[#CE1126]" />
              <div className="flex-1">
                <p className="text-sm font-medium">Private Career Progress Calls</p>
                <p className="text-xs text-gray-500">
                  {ent.career_calls.remaining} of {ent.career_calls.total} calls remaining · scheduled manually
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Available Career Tools */}
        <h2 className="text-lg font-bold mb-3">Available Career Tools</h2>
        {availableTools.length === 0 ? (
          <p className="text-sm text-gray-500 mb-8">No tools available on your current plan yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="tools-grid">
            {availableTools.map(a => {
              const Icon = a.icon;
              return (
                <Link key={a.benefit} to={a.to} data-testid={`tool-${a.benefit}`}>
                  <Card className="p-4 hover:shadow-lg transition-all cursor-pointer h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-[#009639]">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{a.label}</p>
                        <p className="text-xs text-gray-500">{a.desc}</p>
                        <p className="text-xs font-medium text-[#009639] mt-1">Open →</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {/* AI Career Tools */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">AI Career Tools</h2>
          <span className="text-xs text-gray-500">Elite & Career Success Pro</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="ai-tools-grid">
          {AI_TOOLS.map(t => {
            const Icon = t.icon;
            const u = aiUsage?.[t.key];
            const unlocked = u?.has_access;
            const usageText = u?.limit != null ? `${u.remaining}/${u.limit} left` : (unlocked ? 'Included' : '');
            const card = (
              <Card className={`p-4 h-full transition-all ${unlocked ? 'hover:shadow-lg cursor-pointer' : 'opacity-70'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${unlocked ? 'bg-red-50 text-[#CE1126]' : 'bg-gray-100 text-gray-400'}`}>
                    {unlocked ? <Icon className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{t.label}</p>
                    <p className="text-xs text-gray-500">{t.desc}</p>
                    {unlocked
                      ? <p className="text-xs font-medium text-[#009639] mt-1">{usageText}</p>
                      : <p className="text-xs font-medium text-gray-400 mt-1">Locked · Upgrade</p>}
                  </div>
                </div>
              </Card>
            );
            return unlocked
              ? <Link key={t.key} to={t.to} data-testid={`ai-tool-${t.key}`}>{card}</Link>
              : <Link key={t.key} to="/pricing" data-testid={`ai-tool-${t.key}-locked`}>{card}</Link>;
          })}
        </div>

        {/* Locked Premium Tools */}
        {lockedTools.length > 0 && (
          <>
            <h2 className="text-lg font-bold mb-3">Locked Premium Tools</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="locked-tools-grid">
              {lockedTools.map(a => {
                const Icon = a.icon;
                return (
                  <Link key={a.benefit} to="/pricing" data-testid={`locked-tool-${a.benefit}`}>
                    <Card className="p-4 opacity-80 hover:shadow-lg transition-all cursor-pointer h-full">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                          <Lock className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{a.label}</p>
                          <p className="text-xs text-gray-500">{a.desc}</p>
                          <p className="text-xs font-medium text-gray-400 mt-1">Requires {a.tier} · Upgrade →</p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
            <Card className="p-4 mb-8 bg-gray-50 border border-gray-200 text-center" data-testid="locked-tools-note">
              <p className="text-sm text-gray-600">
                These features require an eligible paid package. <Link to="/pricing" className="text-[#CE1126] font-medium underline">Explore our plans</Link> or contact support at admin@dailyuaejobs.com.
              </p>
            </Card>
          </>
        )}

        {/* Support & Usage benefits */}
        {(plan === 'elite' || plan === 'career_success_pro') && (
          <Card className="p-5 mb-8 bg-red-50 border border-red-200" data-testid="elite-extras">
            <h3 className="font-bold mb-1">{plan === 'career_success_pro' ? 'Career Success Pro Support' : 'Elite Support'}</h3>
            <p className="text-sm text-gray-700 mb-2">Priority Support · Target Role Strategy · Application Strategy · HR Follow-Up Guidance · Referral consideration when relevant opportunities are available.{plan === 'career_success_pro' ? ' · 90-Day Personalized Career Roadmap · Priority WhatsApp Support.' : ''}</p>
            <p className="text-xs text-gray-500 italic">Referral consideration is NOT a guaranteed referral or guaranteed job. Private calls are scheduled manually.</p>
          </Card>
        )}

        {/* Compare / Upgrade Packages */}
        {plans.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold">Compare &amp; Upgrade Packages</h2>
              <Link to="/pricing" className="text-xs text-[#CE1126] font-medium underline" data-testid="see-full-pricing">See full pricing</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="compare-packages">
              {plans.map(p => {
                const isCurrent = p.key === plan;
                return (
                  <Card key={p.key} className={`p-4 flex flex-col h-full ${isCurrent ? 'border-2 border-[#CE1126]' : 'border border-gray-200'}`} data-testid={`package-${p.key}`}>
                    <div className="mb-2">
                      <p className="font-bold text-sm">{p.label}</p>
                      <p className="text-xl font-bold">{p.price_aed === 0 ? 'Free' : `AED ${p.price_aed}`}</p>
                    </div>
                    <ul className="space-y-1 mb-4 flex-1">
                      {(p.features || []).slice(0, 6).map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-gray-600">
                          <Check className="w-3 h-3 text-[#009639] mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <span className="text-xs text-center font-medium text-[#CE1126] py-2" data-testid={`package-${p.key}-current`}>Your Plan</span>
                    ) : (
                      <Link to="/pricing">
                        <Button size="sm" variant={p.price_aed === 0 ? 'outline' : 'default'} className={`w-full text-xs ${p.price_aed === 0 ? '' : 'btn-uae-red'}`} data-testid={`package-${p.key}-cta`}>
                          {p.price_aed === 0 ? 'Free Tier' : 'View / Upgrade'}
                        </Button>
                      </Link>
                    )}
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {plan === 'free' && (
          <Card className="p-6 mb-4 bg-gradient-to-r from-[#CE1126] to-[#009639] text-white text-center" data-testid="upgrade-cta">
            <h3 className="text-xl font-bold mb-2">Unlock Professional CV Optimization</h3>
            <p className="text-sm mb-3 opacity-90">Get a Gulf/UAE recruiter-quality CV starting at just AED 99.</p>
            <Link to="/pricing"><Button className="bg-white text-gray-900 hover:bg-gray-100">See Plans</Button></Link>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
